/**
 * Automation Status Card Component
 * 
 * Dashboard card showing overall automation status for staff.
 * Part of the 90% Automation System visibility UI.
 */

import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/api';

interface AutomationStats {
  total_cases: number;
  ready_cases: number;
  auto_ready_cases: number;
  blocked_cases: number;
  pending_action_items: number;
  avg_case_strength: number;
}

interface AutomationStatusCardProps {
  className?: string;
}

export const AutomationStatusCard: React.FC<AutomationStatusCardProps> = ({
  className = ''
}) => {
  const [stats, setStats] = useState<AutomationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await apiFetch('/api/v1/attorney-ai/dashboard-stats');
        const data = response.data || response;
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch automation stats:', err);
        // Set default stats if endpoint doesn't exist yet
        setStats({
          total_cases: 0,
          ready_cases: 0,
          auto_ready_cases: 0,
          blocked_cases: 0,
          pending_action_items: 0,
          avg_case_strength: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className={`bg-[var(--color-bg-surface)] rounded-xl shadow-sm border border-[var(--color-border)] p-6 animate-pulse ${className}`}>
        <div className="h-6 bg-[var(--color-bg-elevated)] rounded w-1/3 mb-4" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-[var(--color-bg-base)] rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const readyPercentage = stats.total_cases > 0
    ? Math.round((stats.ready_cases / stats.total_cases) * 100)
    : 0;

  // Guard the empty state: avg_case_strength is an average over cases, so with zero
  // cases it arrives as NaN (divide-by-zero upstream) and renders "NaN" + an invalid
  // width. Coerce any non-finite value to 0.
  const avgStrength = Number.isFinite(stats.avg_case_strength) ? stats.avg_case_strength : 0;

  return (
    <div className={`bg-[var(--color-bg-surface)] rounded-xl shadow-sm border border-[var(--color-border)] p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-[rgba(255,102,0,0.12)] rounded-lg">
            <svg className="w-5 h-5 text-[var(--color-brand)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h3 className="font-serif font-semibold text-[var(--color-text-primary)]">Automation Status</h3>
            <p className="text-xs text-[var(--color-text-muted)]">90% Automation System</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-mono font-bold text-[var(--color-brand)]">{readyPercentage}%</div>
          <div className="text-xs text-[var(--color-text-muted)]">Cases Ready</div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Ready */}
        <div className="bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded-lg p-3 text-center">
          <div className="text-2xl font-mono font-bold text-[var(--color-verified)]">{stats.ready_cases}</div>
          <div className="text-xs text-[var(--color-text-muted)]">Ready</div>
        </div>

        {/* Auto-Ready */}
        <div className="bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded-lg p-3 text-center">
          <div className="text-2xl font-mono font-bold text-[var(--color-verified)]">{stats.auto_ready_cases}</div>
          <div className="text-xs text-[var(--color-text-muted)]">Auto-Ready</div>
        </div>

        {/* Blocked */}
        <div className="bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded-lg p-3 text-center">
          <div className="text-2xl font-mono font-bold text-[var(--color-needs-human-text)]">{stats.blocked_cases}</div>
          <div className="text-xs text-[var(--color-text-muted)]">Blocked</div>
        </div>

        {/* Pending Items */}
        <div className="bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded-lg p-3 text-center">
          <div className="text-2xl font-mono font-bold text-[var(--color-needs-human-text)]">{stats.pending_action_items}</div>
          <div className="text-xs text-[var(--color-text-muted)]">Pending Items</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-[var(--color-text-muted)] mb-1">
          <span>Case Readiness Progress</span>
          <span className="font-mono text-[var(--color-text-secondary)]">{stats.ready_cases} / {stats.total_cases}</span>
        </div>
        <div className="h-2 bg-[var(--color-bg-elevated)] rounded-full overflow-hidden">
          <div className="h-full flex">
            <div
              className="bg-[var(--color-verified)] transition-all duration-500"
              style={{ width: `${stats.total_cases > 0 ? (stats.auto_ready_cases / stats.total_cases) * 100 : 0}%` }}
            />
            <div
              className="bg-[var(--color-verified)] transition-all duration-500"
              style={{ width: `${stats.total_cases > 0 ? ((stats.ready_cases - stats.auto_ready_cases) / stats.total_cases) * 100 : 0}%` }}
            />
            <div
              className="bg-[var(--color-needs-human-text)] transition-all duration-500"
              style={{ width: `${stats.total_cases > 0 ? (stats.blocked_cases / stats.total_cases) * 100 : 0}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between mt-1">
          <div className="flex items-center gap-4 text-xs text-[var(--color-text-muted)]">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[var(--color-verified)]" /> Auto-Ready
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[var(--color-verified)]" /> Ready
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[var(--color-needs-human-text)]" /> Blocked
            </span>
          </div>
        </div>
      </div>

      {/* Avg Strength */}
      <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
        <div className="flex items-center justify-between">
          <span className="text-sm text-[var(--color-text-muted)]">Average Case Strength</span>
          <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-[var(--color-bg-elevated)] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  avgStrength >= 90 ? 'bg-[var(--color-verified)]' :
                  avgStrength >= 70 ? 'bg-[var(--color-brand)]' :
                  avgStrength >= 50 ? 'bg-[var(--color-needs-human-text)]' : 'bg-[var(--color-needs-human-text)]'
                }`}
                style={{ width: `${avgStrength}%` }}
              />
            </div>
            <span className="font-mono font-bold text-[var(--color-text-secondary)]">{Math.round(avgStrength)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationStatusCard;
